import React, { createContext, useContext, useState } from 'react';
import { useFirebase } from './FirebaseContext';
import { jadPayments } from '../utils/JadPaymentManager';

const PaymentMethodsContext = createContext();

export function PaymentMethodsProvider({ children }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { functions, auth } = useFirebase();

  const loadPaymentMethods = async () => {
    const user = auth.currentUser;
    if (!user || !functions) {
      return;
    }

    setLoading(true);
    try {
      const cards = await jadPayments(functions).listCards();
      setPaymentMethods(cards.map((c) => ({ ...c, brand: 'card', isActive: true })));
      setError(null);
    } catch (err) {
      console.error('Error loading payment methods:', err);
      setPaymentMethods([]);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  // Cards are saved at checkout ("Save this card") — JAD only vaults a card
  // after a successful authorization.
  const addPaymentMethod = async () => {
    throw new Error('Cards are saved at checkout');
  };

  const setDefaultPaymentMethod = async () => {
    // JAD saved cards have no default concept; the picker selects one per order.
  };

  const deletePaymentMethod = async (cardId) => {
    setLoading(true);
    setError(null);
    try {
      await jadPayments(functions).deleteCard(cardId);
      await loadPaymentMethods();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const formatPaymentMethod = (method) => {
    if (!method) return '';
    return `Card •••• ${method.last4}`;
  };

  const getDefaultPaymentMethod = () => {
    return paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];
  };

  const value = {
    paymentMethods,
    loading,
    error,
    addPaymentMethod,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    formatPaymentMethod,
    getDefaultPaymentMethod,
    loadPaymentMethods
  };

  return (
    <PaymentMethodsContext.Provider value={value}>
      {children}
    </PaymentMethodsContext.Provider>
  );
}

export function usePaymentMethods() {
  const context = useContext(PaymentMethodsContext);
  if (!context) {
    throw new Error('usePaymentMethods must be used within a PaymentMethodsProvider');
  }
  return context;
}
